import { useLocation, useNavigate } from "react-router-dom";
import { GlobalConfiguration } from "../../../../utils/configurationProvider";
import CardContainer from "../../../_utils/CommonViewAllCard/CardContainer";
import AccountNumberWithBank from "../../../_utils/CommonViewAllCard/CardSubComponent/AccountNumberWithBank";
import CardActionableButton from "../../../_utils/CommonViewAllCard/CardSubComponent/CardActionableButton";
import FormatAmount from "../../../_utils/FormatAmount/FormatAmount";
import TickerAnimation from "../../../_utils/TickerAnimation";
import ICONS from "../../../../assets/Icons";
import TextWithIcon from "../../../_utils/CommonViewAllCard/CardSubComponent/TextWithIcon";

const ScheduleInternalTransactionViewAllCard = ({
  openTxnHistory,
  data,
  txnHistorytype,
  currentStatus,
  onClose,
  filterRef,
  setLoading,
  isLabelClicked = false,
  labelTransactions = [],
  handleLabelSelect,
  flowType,
  txnId
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "var(--pending)";
      case "approved":
        return "var(--awaiting)";
      case "denied":
      case "failed":
      case "rejected":
        return "var(--failed)";
      case "abandoned":
        return "var(--abandoned)";
      case "hold":
      case "queued":
        return "var(--queued)";
      default:
        return "var(--settled)";
    }
  };

  const getFormattedFrequency = (txn) => {
    const startDate = txn?.transaction_start_date;
    const frequency = txn?.frequency?.toLowerCase();

    if (!frequency) return "";

    if (!startDate && !["daily"].includes(frequency)) return "";

    const dateObj = startDate ? new Date(startDate) : null;

    const day = dateObj?.getDate();
    const weekday = dateObj?.toLocaleString("en-IN", { weekday: "short" });
    const month = dateObj?.toLocaleString("en-IN", { month: "short" });
    const year = dateObj?.getFullYear();

    const formatShortDate = () => `${day} ${month}`;
    const formatFullDate = () => `${day} ${month} ${year}`;

    switch (frequency) {
      case "weekly":
        // Weekly, on Mon
        return `on ${weekday}`;

      case "yearly":
        // Yearly, on 30 Mar
        return `on ${formatShortDate()}`;

      case "monthly":
      case "quarterly":
      case "half_yearly":
      case "fortnightly":
        // Quarterly, from 5 Jan 2025
        return `from ${formatFullDate()}`;

      case "once":
        // Once, on 30 Mar 2025
        return `on ${formatFullDate()}`;

      case "daily":
        // Daily → no date text
        return "";

      default:
        return "";
    }
  };

  const shouldBeRedacted = (price) => {
    return (
      price === "xxxx.xx" ||
      isNaN(+price) ||
      price === null ||
      price === undefined
    );
  };

  const isSelected = (txnId) =>
    Array.isArray(labelTransactions) &&
    labelTransactions.some((t) => (t?.id ?? t?.txn_id) === txnId);

  return (
    <div className={`standard-card-alignment  gap-x-gap-sm gap-y-gap-md`}>
      {data?.map((transactions) => {
        const freq = transactions?.frequency?.toLowerCase();

        const formattedFreq =
          freq === "half_yearly"
            ? "Half yearly"
            : freq
              ? freq.charAt(GlobalConfiguration.lengthCheckZero).toUpperCase() +
                freq.slice(GlobalConfiguration.lengthCheckOne)
              : "";

        const formattedDate = getFormattedFrequency(transactions);

        const frequencyText = formattedDate
          ? `${formattedFreq}, ${formattedDate}`
          : formattedFreq;
        const getApprovalStatusText = (txn, currentStatus) => {
          if (txn?.lifecycle_status === "deleted") return "Discarded";
          if (currentStatus === "Require approval") return "Require approval";
          return txn?.lifecycle_status || "N/A";
        };

        const statusText = getApprovalStatusText(transactions, currentStatus);
        return (
          <CardContainer
            filterRef={filterRef}
            hoverHeight={
              GlobalConfiguration?.internalScheduleTxnCardHoverHeight
            }
            isDisabled={flowType === "labelsSearch" ? transactions?.txn_id === txnId : false}
            isSelected={
              Array.isArray(labelTransactions) &&
              labelTransactions?.some(
                (item) => item?.id === transactions?.txn_id,
              )
            }
           
            key={transactions?.txn_id}
            onClickHandler={() => {
              if (isLabelClicked) {
                handleLabelSelect?.(transactions);
                return;
              }
              setLoading(true);
              if (txnHistorytype === "Schedule_Internal_Txn") onClose()?.call();

              navigate(
                `/internal_transaction_dashboard/schedule_view_all/schedule_detail_page/${transactions.txn_id}`,
                {
                  state: {
                    isViewAll:
                      location.pathname ===
                      "/internal_transaction_dashboard/schedule_view_all"
                        ? true
                        : false,
                  },
                },
              );
            }}
          >
            <card-body>
              <div className="flex flex-col px-padding-sm gap-y-gap-xs">
                <div className="w-full flex justify-between items-center">
                  <p className="text-font-sm text-text-primary flex-1 truncate font-normal">
                    <TickerAnimation text={transactions?.scheduled_name} />
                  </p>

                  <div className="flex gap-x-gap-sm ml-margin-sm justify-end">
                    {transactions?.confidential_transaction === "Y" && (
                      <CardActionableButton
                        icon={ICONS?.CONFIDENTIAL_TRANSACTION}
                        title="confidential"
                        isDisabled={false}
                      />
                    )}
                    {isLabelClicked && (
                      <CardActionableButton
                        icon={
                          isSelected(transactions?.txn_id)
                            ? ICONS?.SELECTED_WITH_GREEN_CIRCLE
                            : ICONS?.SELECT_ALL_WITH_WHITE_OUTLINE_CIRCLE
                        }
                        title={
                          isSelected(transactions?.txn_id)
                            ? "selected"
                            : "select"
                        }
                        isDisabled={false}
                      />
                    )}
                  </div>
                </div>

                <p className="text-font-xs transform-none text-text-secondary">
                  Schedule name
                </p>
              </div>

              <div className="flex flex-col px-padding-sm gap-y-gap-xs">
                <div className="w-full flex justify-between items-center">
                  <p
                    className="text-font-sm flex-1 truncate font-semibold"
                    style={{
                      color: "var(--settled)",
                    }}
                  >
                    {!shouldBeRedacted(transactions?.amount) && "INR"}{" "}
                    <FormatAmount
                      price={Number(
                        String(transactions?.amount)?.replaceAll(",", ""),
                      )}
                    />
                  </p>
                </div>

                <p className="text-font-xs transform-none text-text-secondary">
                  Amount
                </p>
              </div>

              <AccountNumberWithBank
                sourceAccNo={transactions?.owner_account_bank_acc_no}
                destinationAccNo={transactions?.internal_account_bank_acc_no}
                destinationIfsc={transactions?.internal_account_ifsc_code}
                sourceIfsc={transactions?.owner_account_ifsc_code}
              />

              <div className="flex flex-col px-padding-sm gap-y-gap-xs">
                <div className="w-full flex justify-between items-center">
                  <p
                    className="text-font-sm flex-1 truncate font-normal"
                    style={{
                      color: "var(--text-primary)",
                    }}
                  >
                    <TickerAnimation text={frequencyText || "N/A"} />
                  </p>
                </div>

                <p className="text-font-xs transform-none text-text-secondary">
                  Frequency
                </p>
              </div>
            </card-body>
            <card-hover>
              <div className="flex flex-col px-padding-sm gap-y-gap-xs">
                <div className="w-full flex justify-between items-center">
                  <p
                    className="text-font-sm flex-1 truncate font-normal"
                    style={{
                      textTransform: "capitalize",
                      color:
                        transactions?.lifecycle_status === "deleted"
                          ? "var(--abandoned)"
                          : getStatusColor(transactions?.lifecycle_status),
                    }}
                  >
                    {statusText}
                  </p>
                </div>
                <p className="text-font-xs transform-none text-text-secondary">
                  Approval status
                </p>
              </div>
              <TextWithIcon
                text={transactions?.labels?.join(", ") || "No labels assigned"}
                fontWeight={"normal"}
                textColor={"var(--text-primary)"}
                iconComponentArray={[
                  {
                    icon: ICONS?.LABEL,
                  },
                ]}
              />
            </card-hover>
          </CardContainer>
        );
      })}
    </div>
  );
};

export default ScheduleInternalTransactionViewAllCard;
